import { Fragment } from "react";
import { Link, useParams } from "react-router-dom";
import { Figure } from "../components/Figure";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { usePublishedNews } from "../lib/publishedNews";
import { descriptionBlocks } from "../lib/eventDescription";
import { useScrollPastHero } from "../lib/useScrollPastHero";

/** One of webadmin's published news posts, as published (see publishedNews.ts). */
export default function NewsArticle() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { posts, loading } = usePublishedNews();
  const n = posts.find((p) => p.slug === slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (loading) return null;

  if (!n) {
    return (
      <section className="news-article-hero">
        <div className="container">
          <div className="na-eyebrow">
            <span className="cat">404</span>
            <Link to={ROUTES.news}>← Back to news</Link>
          </div>
          <h1 className="na-title">Article not found.</h1>
        </div>
      </section>
    );
  }

  const related = posts.filter((x) => x.slug !== n.slug && n.category && x.category === n.category).slice(0, 3);
  const others = related.length > 0 ? related : posts.filter((x) => x.slug !== n.slug).slice(0, 3);
  const date = `${n.date.day} ${n.date.month} ${n.date.year}`;

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={[n.category, `${n.date.day} ${n.date.month}`].filter(Boolean).join(" · ")}
        title={n.title}
        back={{ label: "All news", to: ROUTES.news }}
      />
      <section className="news-article-hero" ref={heroRef}>
        <div className="container">
          <div className="na-eyebrow">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.news}>News</Link>
            {n.category && (
              <>
                <span aria-hidden>/</span>
                <span className="cat">{n.category}</span>
              </>
            )}
          </div>
          <h1 className="na-title">{n.title}</h1>
          {n.summary && <p className="na-summary">{n.summary}</p>}
          <div className="na-byline">
            <span>
              <b>{n.date.day}</b> {n.date.month} {n.date.year}
            </span>
            {n.authorName && <span>By {n.authorName}</span>}
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              {n.cover && (
                <div className="news-article-image">
                  <Figure media={n.cover} fallbackLabel={n.title} aspect="21/9" sizes="(max-width: 1000px) 100vw, 760px" priority />
                </div>
              )}

              <div className="news-article-body">
                {descriptionBlocks(n.content).map((b, i) => (
                  <Fragment key={i}>
                    {b.text && <p>{b.text}</p>}
                    {b.list && (
                      <ul>
                        {b.list.map((item, j) => (
                          <li key={j}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </Fragment>
                ))}
              </div>
            </div>

            <aside className="detail-meta-card is-accented">
              {n.category && (
                <div className="dmc-block">
                  <div className="dmc-label">Filed under</div>
                  <div className="dmc-value">{n.category}</div>
                </div>
              )}
              <div className="dmc-block">
                <div className="dmc-label">Date</div>
                <div className="dmc-value">{date}</div>
              </div>
              {n.authorName && (
                <div className="dmc-block">
                  <div className="dmc-label">Author</div>
                  <div className="dmc-value">{n.authorName}</div>
                </div>
              )}
              {n.departmentName && (
                <div className="dmc-block">
                  <div className="dmc-label">Department</div>
                  <div className="dmc-value">
                    {n.departmentSlug ? (
                      <Link to={`${ROUTES.department}/${n.departmentSlug}`}>{n.departmentName}</Link>
                    ) : (
                      n.departmentName
                    )}
                  </div>
                </div>
              )}
              {n.externalUrl && (
                <div className="dmc-block">
                  <div className="dmc-label">Source</div>
                  <div className="dmc-value">
                    <a href={n.externalUrl} target="_blank" rel="noreferrer noopener" aria-label="Read the original (external site)">
                      Read the original <span aria-hidden="true">↗</span>
                    </a>
                  </div>
                </div>
              )}
              <div className="dmc-block">
                <div className="dmc-label">Story archive</div>
                <div className="dmc-value">
                  <Link to={ROUTES.news}>Browse all news →</Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">More news</div>
              <h2>{related.length > 0 ? `More on ${n.category}` : "Other recent stories"}</h2>
            </div>
            <div className="er-grid">
              {others.map((r) => (
                <Link key={r.slug} to={`${ROUTES.news}/${r.slug}`} className="er-card">
                  <div className="er-date">
                    <span className="er-day">{r.date.day}</span>
                    <span className="er-month">{r.date.month}</span>
                  </div>
                  <div className="er-body">
                    {r.category && <span className="er-tag">{r.category}</span>}
                    <h3>{r.title}</h3>
                    <span className="er-venue">{r.date.year}</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
